import type { HTMLAttributes } from 'react';
import { forwardRef } from 'react';

import { cn } from '@/lib/utils';
import { useCssDimensions } from '@/utils/hooks/useCssDimensions';

import { ImageLink } from '@/components/atoms/ImageLink';
import { LocalImage } from '@/components/atoms/LocalImage';
import { IconsLinksList } from '@/components/molecules/IconsLinksList';
import { MobileNavigationList } from '@/components/molecules/MobileNavigationList';

import type { FooterConfig } from '../../Footer.types';

interface FooterMobileProps
    extends Omit<FooterConfig, 'footerInfo' | 'promoCodesModal'>,
        HTMLAttributes<HTMLDivElement> {
    theme: string | undefined;
    location: string;
}

const FooterMobile = forwardRef<HTMLDivElement, FooterMobileProps>(
    ({ theme, links, iconsListData, location, className }, ref) => {
        const { minWidthLg } = useCssDimensions();

        return (
            <div
                ref={ref}
                className={cn(
                    'border-t border-grey-100 pb-12 pt-12',
                    className,
                )}
            >
                <div className="mb-8 flex justify-between">
                    <LocalImage
                        src={
                            theme === 'dark'
                                ? '/images/logos/logo-white.svg'
                                : '/images/logos/logo.svg'
                        }
                        alt="PayPo"
                        width={85}
                        height={23}
                        priority
                    />

                    {!!links[0]?.socialLinks && !minWidthLg && (
                        <IconsLinksList
                            variant="horizontalLeft"
                            iconsData={links[0].socialLinks}
                            className="lg:hidden"
                        />
                    )}

                    {!!links[0]?.partnerLogo && (
                        <ImageLink {...links[0].partnerLogo} />
                    )}
                </div>

                <MobileNavigationList
                    links={links}
                    location={location}
                    navigationLinkVariant={{
                        color: 'secondary',
                        weight: 'normal',
                        states: 'enable',
                        className: 'text-2xs',
                    }}
                    accordionClassName="text-sm"
                    navigationLinkClassName="pb-4"
                    className="w-full"
                />

                {!!iconsListData && (
                    <IconsLinksList
                        variant="vertical"
                        iconsData={iconsListData.links}
                        className="mt-8 justify-start lg:justify-center"
                    />
                )}
            </div>
        );
    },
);

export default FooterMobile;
